/**
 * Lightweight SSE consumer for `POST /api/v1/chat/conversations/{id}/stream`.
 *
 * EventSource doesn't support POST, so we use fetch + ReadableStream and parse
 * the SSE wire format manually:
 *
 *   event: <name>
 *   data:  <json>
 *   <blank line>
 *
 * Secretary emits `thinking`, `message`, `done`, `error` events — see chat_bll.py
 * § 4 for the canonical list.
 */

import type { SSEEvent } from '@/types/chat';
import { authHeader } from './auth';

export interface StreamCallbacks {
  onEvent: (ev: SSEEvent) => void;
  onError?: (err: Error) => void;
  onClose?: () => void;
}

export interface StreamHandle {
  cancel: () => void;
}

export function streamChat(
  conversationId: string,
  message: string,
  cb: StreamCallbacks,
  opts: { reasoningLevel?: 'auto' | 'fast' | 'deep' } = {},
): StreamHandle {
  const ctrl = new AbortController();

  (async () => {
    try {
      const resp = await fetch(
        `/api/v1/chat/conversations/${conversationId}/stream`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...authHeader(),
          },
          body: JSON.stringify({
            message,
            reasoning_level: opts.reasoningLevel ?? 'auto',
          }),
          signal: ctrl.signal,
        },
      );

      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Stream failed: ${resp.status} ${text.slice(0, 200)}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by blank lines (\n\n).
        let eventBoundary = buffer.indexOf('\n\n');
        while (eventBoundary !== -1) {
          const rawEvent = buffer.slice(0, eventBoundary);
          buffer = buffer.slice(eventBoundary + 2);
          eventBoundary = buffer.indexOf('\n\n');

          const parsed = parseSSEFrame(rawEvent);
          if (parsed) {
            try {
              cb.onEvent(parsed);
            } catch (e) {
              // Swallow callback errors so a buggy renderer can't kill the stream.
              console.error('SSE onEvent threw', e);
            }
          }
        }
      }

      cb.onClose?.();
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        cb.onClose?.();
        return;
      }
      cb.onError?.(err as Error);
    }
  })();

  return { cancel: () => ctrl.abort() };
}

/**
 * Parse one SSE frame like:
 *   event: message
 *   data: {"content": "Hi"}
 * Returns null if the frame is malformed or has no `event:` line.
 */
function parseSSEFrame(raw: string): SSEEvent | null {
  let eventName = 'message';
  let dataLine = '';

  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLine += line.slice(5).trim();
    }
  }

  if (!dataLine) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(dataLine);
  } catch {
    // Tolerate plain-text data lines for `message` events.
    if (eventName === 'message') {
      parsed = { content: dataLine };
    } else {
      return null;
    }
  }

  switch (eventName) {
    case 'thinking':
    case 'progress':
    case 'tool_completed':
    case 'message':
    case 'done':
    case 'error':
      return { event: eventName, data: parsed } as SSEEvent;
    default:
      // Unknown event types are surfaced as 'thinking' for visibility.
      return { event: 'thinking', data: { stage: eventName } } as SSEEvent;
  }
}
